import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { getAllCategories, deleteCategory } from '../../firebase/models/blogService';

const Container = styled.div`
  padding: var(--spacing-xl);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-xl);
`;

const Title = styled.h1`
  margin: 0;
`;

const Button = styled(Link)`
  padding: var(--spacing-sm) var(--spacing-md);
  background-color: var(--accent-blue);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  text-decoration: none;
  display: inline-block;
  
  &:hover {
    background-color: var(--dark-blue);
  }
`;

const Card = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: var(--spacing-md);
  border-bottom: 2px solid #eee;
`;

const Td = styled.td`
  padding: var(--spacing-md);
  border-bottom: 1px solid #eee;
`;

const ActionButton = styled.button`
  padding: var(--spacing-sm);
  margin-right: var(--spacing-sm);
  background-color: ${props => props.delete ? '#ff4d4d' : '#f0f0f0'};
  color: ${props => props.delete ? 'white' : 'inherit'};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.delete ? '#e60000' : '#e0e0e0'};
  }
`;

const ActionLink = styled(Link)`
  padding: var(--spacing-sm);
  margin-right: var(--spacing-sm);
  background-color: #f0f0f0;
  color: inherit;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
  
  &:hover {
    background-color: #e0e0e0;
  }
`;

const Message = styled.p`
  padding: var(--spacing-md);
  background-color: ${props => props.type === 'error' ? '#ffeeee' : '#eeffee'};
  border-radius: 4px;
  margin-bottom: var(--spacing-md);
`;

const EmptyState = styled.div`
  padding: var(--spacing-xl);
  text-align: center;
  color: #888;
`;

const formatDate = (timestamp) => {
  if (!timestamp) return 'Unknown date';
  
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const truncate = (text, length = 50) => {
  if (!text) return '';
  return text.length > length ? text.substring(0, length) + '...' : text;
};

const CategoryList = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const categoriesData = await getAllCategories();
      setCategories(categoriesData);
      setError(null);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchCategories();
  }, []);
  
  const handleDelete = async (categoryId, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await deleteCategory(categoryId);
        setSuccess(`Category "${name}" was deleted successfully.`);
        fetchCategories(); // Refresh the list
      } catch (err) {
        console.error('Error deleting category:', err);
        setError('Failed to delete category. Please try again.');
      }
    }
  };
  
  return (
    <Container>
      <Header>
        <Title>Manage Categories</Title>
        <Button to="/admin/categories/new">Create New Category</Button>
      </Header>
      
      {error && <Message type="error">{error}</Message>}
      {success && <Message>{success}</Message>}
      
      <Card>
        {loading ? (
          <p>Loading categories...</p>
        ) : categories.length === 0 ? (
          <EmptyState>
            <p>No categories found. Start by creating your first category.</p>
            <Button to="/admin/categories/new">Create New Category</Button>
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Slug</Th>
                <Th>Created Date</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {categories.map(category => (
                <tr key={category.id}>
                  <Td>{category.name}</Td>
                  <Td>{category.slug}</Td>
                  <Td>{formatDate(category.createdAt)}</Td>
                  <Td>
                    <ActionLink to={`/admin/categories/edit/${category.id}`}>Edit</ActionLink>
                    <ActionButton 
                      delete
                      onClick={() => handleDelete(category.id, category.name)}
                    >
                      Delete
                    </ActionButton>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </Container>
  );
};

export default CategoryList; 